import { type Redirect, type Rewrite, type RouteHas } from 'next/dist/lib/load-custom-routes';

/**
 * Organizer/Integrator Hosts matchers
 */
export const orgIntegratorHosts = {
	unitedTickets: [{ type: 'host', value: 'store.united-tickets.cz' }],
	lovestreamTickets: [{ type: 'host', value: 'tickets.lovestream.sk' }],
	miraiFest: [{ type: 'host', value: 'vstupenky.miraifest.cz' }],
	chinaski: [{ type: 'host', value: 'vstupenky.chinaski.cz' }],
	marekZtraceny: [{ type: 'host', value: 'vstupenky.marekztraceny.cz' }],
	hradyCZ: [{ type: 'host', value: 'vstupenky.hradycz.cz' }],
	mklvFest: [{ type: 'host', value: 'vstupenky.mklvfest.cz' }],
	pragueHarleyDays: [{ type: 'host', value: 'vstupenky.pragueharleydays.cz' }],
	pragueOpenAir: [{ type: 'host', value: 'vstupenky.pragueopenair.cz' }],
	soldout: [{ type: 'host', value: 'vstupenky.soldoutfestival.cz' }],
	symphonika: [{ type: 'host', value: 'vstupenky.symphonika.cz' }],
} as const satisfies Record<string, RouteHas[]>;

export type OrgIntegratorHost = keyof typeof orgIntegratorHosts;

function createOrgIntegratorRewrite(input: { organizerSlugs: Array<string>; tourSlugs: Array<string>; has: Array<RouteHas>; index: string }) {
	const eventSource =
		input.organizerSlugs.length === 0 ? '/event/:organizer(.*)/:slug(.*)?' : `/event/:organizer((?!${input.organizerSlugs.join('|')}).*)/:slug(.*)?`;
	const organizerSource =
		input.organizerSlugs.length === 0 ? '/organizer/:organizer(.*)?' : `/organizer/:organizer((?!${input.organizerSlugs.join('|')}).*)?`;
	const tourSource = input.tourSlugs.length === 0 ? '/tour/:eventGroup(.*)?' : `/tour/:eventGroup((?!${input.tourSlugs.join('|')}).*)?`;

	return {
		/** prevent other organizer's events from being shown on this domain */
		preventEvents: {
			type: 'rewrite',
			source: eventSource,
			destination: '/',
			has: input.has,
		},
		preventOrganizer: {
			type: 'rewrite',
			source: organizerSource,
			destination: '/',
			has: input.has,
		},
		preventTour: {
			type: 'rewrite',
			source: tourSource,
			destination: '/',
			has: input.has,
		},
		/** rewrite to the organizer's main page */
		index: {
			type: 'rewrite',
			source: '/',
			destination: input.index ?? '/',
			has: input.has,
		},
	} as const satisfies Record<string, RewriteOrRedirect>;
}

type RewriteType = Rewrite & { type: 'rewrite' };
type RedirectType = Redirect & { type: 'redirect' };
type RewriteOrRedirect = RewriteType | RedirectType;

/**
 * Managed rewrite and redirect rules
 * @see https://nextjs.org/docs/pages/api-reference/config/next-config-js/rewrites
 * @see https://nextjs.org/docs/pages/api-reference/config/next-config-js/redirects
 */
export const orgIntegratorRewritesAndRedirects = [
	/** United Tickets integrator specific rewrite rules */
	{
		...createOrgIntegratorRewrite({
			organizerSlugs: ['universal-music'],
			tourSlugs: ['vesna-muzika-slavica-2025', 'poetika-2025', 'gaia-mesiah-elements-tour', 'dorian-bmw-backseat-tour'],
			has: orgIntegratorHosts.unitedTickets,
			index: '/',
		}),
		preventMiraiEvents: {
			type: 'rewrite',
			source: '/event/universal-music/mirai-summer-fest-2025(.*)',
			destination: '/',
			has: orgIntegratorHosts.unitedTickets,
		},
		index: {
			type: 'redirect',
			source: '/',
			destination: 'https://united-tickets.cz',
			has: orgIntegratorHosts.unitedTickets,
			permanent: false,
		},
		poaJamaron: {
			type: 'redirect',
			source: '/event/universal-music/jamaron-plovarna-2025',
			has: orgIntegratorHosts.unitedTickets,
			destination: 'https://vstupenky.pragueopenair.cz/event/prague-open-air-sro/jamaron-2025',
			permanent: true,
		},
	},
	/** MIRAIFEST Tickets integrator specific rewrite rules */
	{
		...createOrgIntegratorRewrite({
			organizerSlugs: ['universal-music'],
			tourSlugs: ['mirai-summer-fest-2025'],
			has: orgIntegratorHosts.miraiFest,
			index: '/tour/mirai-summer-fest-2025',
		}),
		preventOtherUniversalMusicEvents: {
			type: 'rewrite',
			source: '/event/universal-music((?!\\/mirai-summer-fest-2025).*)',
			destination: '/',
			has: orgIntegratorHosts.miraiFest,
		},
		events: {
			type: 'rewrite',
			source: '/koncert/:slug(.*)',
			destination: '/event/universal-music/:slug',
			has: orgIntegratorHosts.miraiFest,
		},
		terms: {
			type: 'rewrite',
			source: '/obchodni-podminky',
			destination: '/tour/mirai-summer-fest-2025/terms-and-conditions',
			has: orgIntegratorHosts.miraiFest,
		},
	},
	/** Chinaski 2025 integrator specific rewrite rules */
	{
		...createOrgIntegratorRewrite({
			organizerSlugs: ['matchfire-sro'],
			tourSlugs: ['chinaski-2025'],
			has: orgIntegratorHosts.chinaski,
			index: '/tour/chinaski-2025',
		}),
		events: {
			type: 'rewrite',
			source: '/koncert/:slug(.*)',
			destination: '/event/matchfire-sro/:slug',
			has: orgIntegratorHosts.chinaski,
		},
		terms: {
			type: 'rewrite',
			source: '/obchodni-podminky',
			destination: '/tour/chinaski-2025/terms-and-conditions',
			has: orgIntegratorHosts.chinaski,
		},
	},
	/** Marek Ztracený 2025 integrator specific rewrite rules */
	{
		...createOrgIntegratorRewrite({
			organizerSlugs: ['marek-ztraceny'],
			tourSlugs: ['marek-ztraceny-je-zpatky-tour-2025'],
			has: orgIntegratorHosts.marekZtraceny,
			index: '/tour/marek-ztraceny-je-zpatky-tour-2025',
		}),
		indexRodina: {
			type: 'rewrite',
			source: '/ztracena-rodina',
			destination: '/tour/marek-ztraceny-je-zpatky-tour-2025?promoCode=rodina',
			has: orgIntegratorHosts.marekZtraceny,
		},
		events: {
			type: 'rewrite',
			source: '/koncert/:slug(.*)',
			destination: '/event/marek-ztraceny/:slug',
			has: orgIntegratorHosts.marekZtraceny,
		},
		terms: {
			type: 'rewrite',
			source: '/obchodni-podminky',
			destination: '/tour/marek-ztraceny-je-zpatky-tour-2025/terms-and-conditions',
			has: orgIntegratorHosts.marekZtraceny,
		},
	},
	/** HradyCZ integrator specific rewrite rules */
	{
		...createOrgIntegratorRewrite({
			organizerSlugs: ['riff-raff-sro'],
			tourSlugs: ['hrady-cz-2025'],
			has: orgIntegratorHosts.hradyCZ,
			index: '/tour/hrady-cz-2025',
		}),
		events: {
			type: 'rewrite',
			source: '/festival/:slug(.*)',
			destination: '/event/riff-raff-sro/:slug',
			has: orgIntegratorHosts.hradyCZ,
		},
		terms: {
			type: 'rewrite',
			source: '/obchodni-podminky',
			destination: '/tour/hrady-cz-2025/terms-and-conditions',
			has: orgIntegratorHosts.hradyCZ,
		},
	},
	/** MKLV Fest 2025 integrator specific rewrite rules */
	createOrgIntegratorRewrite({
		organizerSlugs: ['art-delivery'],
		tourSlugs: [],
		has: orgIntegratorHosts.mklvFest,
		index: '/event/art-delivery/mklv-fest-2025',
	}),
	/** LOVESTREAM Tickets integrator specific rewrite rules */
	createOrgIntegratorRewrite({
		organizerSlugs: ['silvi-production'],
		tourSlugs: [],
		has: orgIntegratorHosts.lovestreamTickets,
		index: '/event/silvi-production/lovestream-festival-2025',
	}),
	/** Prague Harley Days integrator specific rewrite rules */
	createOrgIntegratorRewrite({
		organizerSlugs: ['prague-harley-days-1649858729634'],
		tourSlugs: [],
		has: orgIntegratorHosts.pragueHarleyDays,
		index: '/event/prague-harley-days-1649858729634/prague-harley-days-2025',
	}),
	/** Prague Open Air integrator specific rewrite rules */
	createOrgIntegratorRewrite({
		organizerSlugs: ['prague-open-air'],
		tourSlugs: ['prague-open-air'],
		has: orgIntegratorHosts.pragueOpenAir,
		index: '/tour/prague-open-air',
	}),
	/** Soldout 2025 integrator specific rewrite rules */
	createOrgIntegratorRewrite({
		organizerSlugs: ['event4life-sro'],
		tourSlugs: [],
		has: orgIntegratorHosts.soldout,
		index: '/event/event4life-sro/soldout-festival-2025',
	}),
	/* Symphonika On The Rock 2025 */
	createOrgIntegratorRewrite({
		organizerSlugs: ['cbi-production'],
		tourSlugs: ['symphonika-on-the-rock'],
		has: orgIntegratorHosts.symphonika,
		index: '/tour/symphonika-on-the-rock',
	}),
] as const satisfies Array<Record<string, RewriteOrRedirect>>;
